import { axiosConfig, type RequestConfig } from "../shared/api-client";
import type { ApiItemResponse } from "@project/types/location";
import { JIVANDHARA_BASE } from "./list-api-helpers";

const BASE = `${JIVANDHARA_BASE}/payment-settings`;
const SILENT: RequestConfig = { showNotification: false };

export type PaymentSettingsPayload = {
  upi_id: string;
  qr_code_url: string;
  account_name: string;
  bank_name: string;
  account_number: string;
  ifsc_code: string;
  branch: string;
  pan_number: string;
  registration_80g: string;
  trust_registration_number: string;
  trust_address: string;
  contact_phone: string;
  contact_email: string;
};

export type PaymentSettingsRecord = PaymentSettingsPayload & {
  id?: number | string;
  created_at?: string;
  updated_at?: string;
};

export type PaymentSettingsPatch = Partial<PaymentSettingsPayload>;

type PaymentSettingsResponse =
  | ApiItemResponse<PaymentSettingsRecord>
  | PaymentSettingsRecord
  | PaymentSettingsRecord[]
  | { data: PaymentSettingsRecord | PaymentSettingsRecord[] | null }
  | undefined;

function unwrapPaymentSettings(
  response: PaymentSettingsResponse
): PaymentSettingsRecord | undefined {
  if (!response) {
    return undefined;
  }

  if (Array.isArray(response)) {
    return response[0];
  }

  if (typeof response === "object" && "data" in response) {
    const value = response.data;
    if (Array.isArray(value)) {
      return value[0];
    }
    return value ?? undefined;
  }

  return response;
}

class PaymentSettingsService {
  get = async (): Promise<PaymentSettingsRecord | undefined> => {
    const response = await axiosConfig.get<PaymentSettingsResponse>(
      `${BASE}/`,
      SILENT
    );
    return unwrapPaymentSettings(response.data);
  };

  create = async (
    payload: PaymentSettingsPayload
  ): Promise<PaymentSettingsRecord> => {
    const response = await axiosConfig.post<PaymentSettingsResponse>(
      `${BASE}/`,
      payload,
      SILENT
    );
    return unwrapPaymentSettings(response.data) ?? payload;
  };

  update = async (
    id: number | string,
    payload: PaymentSettingsPatch
  ): Promise<PaymentSettingsRecord> => {
    const response = await axiosConfig.patch<PaymentSettingsResponse>(
      `${BASE}/${id}/`,
      payload,
      SILENT
    );
    return (
      unwrapPaymentSettings(response.data) ?? (payload as PaymentSettingsRecord)
    );
  };
}

export const paymentSettingsService = new PaymentSettingsService();
